// src/components/CalendarWidget.jsx
import React, { useMemo, useState } from "react";
import MonthGridCalendar from "./MonthGridCalendar";

// helper: move months forward/back safely across years
function addMonths(year, monthIndex, delta) {
  const d = new Date(year, monthIndex + delta, 1);
  return { year: d.getFullYear(), monthIndex: d.getMonth() };
}

export default function CalendarWidget({
  initialYear,
  initialMonthIndex, // 0 = Jan
  eventsByDate = {},
  minYear = 2000,
  maxYear = 2100
}) {
  const [ym, setYm] = useState(() => ({
    year: initialYear ?? new Date().getFullYear(),
    monthIndex: initialMonthIndex ?? new Date().getMonth()
  }));

  const label = useMemo(() => {
    const d = new Date(ym.year, ym.monthIndex, 1);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  }, [ym.year, ym.monthIndex]);

  const canPrev = ym.year > minYear || (ym.year === minYear && ym.monthIndex > 0);
  const canNext = ym.year < maxYear || (ym.year === maxYear && ym.monthIndex < 11);

  const goPrev = () => {
    if (!canPrev) return;
    setYm((cur) => addMonths(cur.year, cur.monthIndex, -1));
  };

  const goNext = () => {
    if (!canNext) return;
    setYm((cur) => addMonths(cur.year, cur.monthIndex, +1));
  };

  const goToday = () => {
    const now = new Date();
    setYm({ year: now.getFullYear(), monthIndex: now.getMonth() });
  };

  return (
    <div className="q-calWrap">
      <div className="q-calNav" aria-label="Calendar controls">
        <button className="q-calBtn" onClick={goPrev} disabled={!canPrev} aria-label="Previous month">
          ←
        </button>

        <div className="q-calLabel">{label}</div>

        <button className="q-calBtn" onClick={goNext} disabled={!canNext} aria-label="Next month">
          →
        </button>

        <button className="q-calBtn q-calBtn--ghost" onClick={goToday}>
          Today
        </button>
      </div>

      <MonthGridCalendar
        year={ym.year}
        monthIndex={ym.monthIndex}
        eventsByDate={eventsByDate}
        // we pass title so the inner component doesn't need its own header text
        title={label}
      />
    </div>
  );
}